import { Building2, ChevronsUpDown } from 'lucide-react';
import { useCallback } from 'react';
import { Drawer as VaulDrawer } from 'vaul';

import { TeamSwitcherContent } from './team-switcher-content';
import { useAccountSwitcher } from '../../hooks/use-account-switcher';
import { formatPlanDisplayName } from '@/features/billing/helpers/subscription-display-helpers';
import { Button } from '@/components/ui/button';
import { DRAWER_APPEARANCE } from '@/components/ui/types/drawer-variants-enums';
import { DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

interface MobileAccountSwitcherProps {
  onAccountSwitched: () => void;
}

const ITEM_HEIGHT = 44;

export function MobileAccountSwitcher({ onAccountSwitched }: MobileAccountSwitcherProps) {
  const switcher = useAccountSwitcher();
  const currentAccountId = switcher.currentAccount?.id;

  const selectAccount = useCallback(
    async (accountId: string) => {
      await switcher.handleSwitchAccount(accountId);
      if (accountId !== currentAccountId) {
        onAccountSwitched();
      }
    },
    [switcher, currentAccountId, onAccountSwitched]
  );

  const handleSelect = useCallback(
    (accountId: string) => {
      void selectAccount(accountId);
    },
    [selectAccount]
  );

  const handleAddNewCompany = useCallback(() => {
    switcher.handleAddNewCompany();
    onAccountSwitched();
  }, [switcher, onAccountSwitched]);

  const accountName = switcher.currentAccount?.accountName ?? 'Workspace';
  const rawPlanName = switcher.currentAccount?.subscription?.subscriptionPlan?.displayName;
  const planGroupName = switcher.currentAccount?.subscription?.subscriptionPlanGroup?.displayName;
  const planName = rawPlanName
    ? formatPlanDisplayName(rawPlanName, planGroupName)
    : undefined;

  return (
    <VaulDrawer.NestedRoot open={switcher.open} onOpenChange={switcher.setOpen}>
      <button
        aria-label="Switch workspace"
        className={cn(
          // Embedded in More identity owner — parent supplies surface-base + shadow-surface.
          'flex h-14 w-full items-center gap-3 rounded-control',
          'px-1 text-left transition-[color,background-color] duration-fast ease-standard hover:bg-neutral-interaction-hover motion-reduce:transition-none',
          switcher.isLoading && 'pointer-events-none opacity-70'
        )}
        disabled={switcher.isLoading}
        onClick={() => switcher.setOpen(true)}
        type="button"
      >
        <span
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-neutral-interaction-selected text-content-primary"
        >
          <Building2 className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className="line-clamp-1 truncate text-sm font-medium leading-tight text-content-primary"
            title={accountName}
          >
            {accountName}
          </span>
          {planName ? (
            <span
              className="line-clamp-1 truncate text-xs leading-tight text-content-secondary"
              title={planName}
            >
              {planName}
            </span>
          ) : (
            <span className="text-xs leading-tight text-content-tertiary">Switch workspace</span>
          )}
        </span>
        <ChevronsUpDown
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-content-tertiary"
        />
      </button>
      <DrawerContent appearance={DRAWER_APPEARANCE.FOUNDATION} className="max-h-[85vh]">
        <DrawerHeader>
          <DrawerTitle>Switch workspace</DrawerTitle>
        </DrawerHeader>
        <div className="flex-1 overflow-hidden">
          <TeamSwitcherContent
            accounts={switcher.accounts}
            currentAccountId={currentAccountId}
            filteredAccounts={switcher.filteredAccounts}
            isLoading={switcher.isLoading}
            itemHeight={ITEM_HEIGHT}
            onAddNewCompany={handleAddNewCompany}
            onSearchChange={switcher.setSearchQuery}
            onSelectAccount={handleSelect}
            searchQuery={switcher.searchQuery}
          />
        </div>
        <DrawerFooter className="pt-2">
          <DrawerClose asChild>
            <Button className="w-full" disabled={switcher.isLoading} variant="outline">
              Close
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </VaulDrawer.NestedRoot>
  );
}
